import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, styled, Theme } from '@mui/material/styles';
import {
  ConfidenceBand,
  ConfidenceExplanation,
  confidenceBand,
  IMAGE_MATCH_CONFIDENCE,
  PERCENT_BAND_RANGES,
  scoreToPercent,
  WORD_BAND_LABELS,
} from './confidence';

const BAND_ORDER: ConfidenceBand[] = ['high', 'medium', 'low'];

const bandColor = (theme: Theme, band: ConfidenceBand): string => {
  const colors: Record<ConfidenceBand, string> = {
    high: theme.palette.success.main,
    medium: theme.palette.warning.main,
    low: theme.palette.error.main,
  };
  return colors[band];
};

const Pill = styled(Box, { shouldForwardProp: property => property !== 'band' })<{ band: ConfidenceBand }>(
  ({ theme, band }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    height: 22,
    padding: '0 8px',
    borderRadius: 11,
    fontSize: 12,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    cursor: 'default',
    color: bandColor(theme, band),
    backgroundColor: alpha(bandColor(theme, band), 0.16),
    border: `1px solid ${alpha(bandColor(theme, band), 0.45)}`,
  })
);

const Dot = styled(Box, { shouldForwardProp: property => property !== 'band' })<{ band: ConfidenceBand }>(
  ({ theme, band }) => ({
    width: 8,
    height: 8,
    marginTop: 5,
    borderRadius: '50%',
    flexShrink: 0,
    backgroundColor: bandColor(theme, band),
  })
);

interface ConfidenceLegendProperties {
  explanation: ConfidenceExplanation;
  ranges: Record<ConfidenceBand, string>;
}

const ConfidenceLegend = ({ explanation, ranges }: ConfidenceLegendProperties) => (
  <Stack gap='6px' sx={{ py: 0.5 }}>
    <Typography variant='body2'>{explanation.summary}</Typography>
    {BAND_ORDER.map(band => (
      <Stack key={band} direction='row' gap='8px'>
        <Dot band={band} />
        <Typography variant='caption'>
          <strong>{ranges[band]}:</strong> {explanation.bands[band]}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

export interface ConfidencePillProperties {
  band: ConfidenceBand;
  label: string;
  explanation: ConfidenceExplanation;
  ranges: Record<ConfidenceBand, string>;
}

export const ConfidencePill = ({ band, label, explanation, ranges }: ConfidencePillProperties) => (
  <Tooltip title={<ConfidenceLegend explanation={explanation} ranges={ranges} />} placement='top'>
    <Pill band={band} data-testid='confidence-pill' data-band={band}>
      {label}
    </Pill>
  </Tooltip>
);

export interface PercentConfidencePillProperties {
  percent: number;
  explanation: ConfidenceExplanation;
}

export const PercentConfidencePill = ({ percent, explanation }: PercentConfidencePillProperties) => (
  <ConfidencePill
    band={confidenceBand(percent)}
    label={`${percent}%`}
    explanation={explanation}
    ranges={PERCENT_BAND_RANGES}
  />
);

export const ImageMatchConfidencePill = ({ score }: { score: number }) => {
  const band = confidenceBand(scoreToPercent(score));
  return (
    <ConfidencePill
      band={band}
      label={WORD_BAND_LABELS[band]}
      explanation={IMAGE_MATCH_CONFIDENCE}
      ranges={WORD_BAND_LABELS}
    />
  );
};
